import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, Loader2, ChevronDown, ChevronUp, LogIn, LogOut,
  BedDouble, Baby, AlertCircle, CheckCircle2,
} from "lucide-react";

/**
 * PublicCleaningPlanPage (iter226)
 *
 * Vista pubblica read-only del piano pulizie del giorno, accessibile
 * dalle collaboratrici tramite il link WhatsApp condiviso dall'host
 * (`/pulizie/:token`). Nessuna autenticazione richiesta — il token
 * espira dopo 3 giorni (gestito dal backend).
 *
 * Design:
 * - Mobile-first (le collaboratrici aprono il link dal telefono)
 * - 3 sezioni collassabili identiche all'admin (arrivi/checkout/stay)
 * - Le sezioni Nuovi Arrivi e Check-Out sono APERTE di default; la
 *   sezione Camere occupate resta chiusa (spesso vuota o meno urgente).
 * - Read-only: nessuna casella modificabile, solo visualizzazione.
 *   Se in futuro l'host vuole tracking di completamento, aggiungiamo
 *   un tasto "Fatto" con endpoint POST /public/cleaning-plan/{token}/done.
 */

const API = process.env.REACT_APP_BACKEND_URL;

const fmtDayLong = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return iso; }
};

const Section = ({ title, count, color, defaultOpen, icon: Icon, children, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-xl border ${color.border} bg-white overflow-hidden shadow-sm`} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full px-4 py-3 flex items-center justify-between ${color.bg} hover:brightness-95 transition`}
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${color.icon}`} />}
          <span className={`font-display text-base ${color.text}`}>{title}</span>
          {count > 0 && (
            <span className={`ml-1 text-white text-xs font-bold rounded-full px-2 py-0.5 ${color.badge}`}>
              {count}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {open && <div className="p-3 space-y-2">{children}</div>}
    </div>
  );
};

// Renders a readonly summary of the cleaning tasks saved by the host
// for a given booking. Falls back to a graceful "nessuna nota" placeholder
// when the host hasn't filled anything.
const ReadOnlyTasks = ({ st, forCheckout = false }) => {
  if (!st) {
    return <div className="text-xs text-stone-500 italic mt-2">Nessuna istruzione specifica.</div>;
  }
  const beds = st.beds || {};
  const cl = st.cleaning || {};
  const notes = st.notes || "";
  const hasBeds = beds.matrimoniali || beds.singoli || beds.letti_aggiunti || beds.culla;
  const hasCleaningFlags = cl.asciugamani || cl.lenzuola || cl.veloce || cl.profonda;
  const empty = !hasBeds && !hasCleaningFlags && !notes;
  if (empty) {
    return <div className="text-xs text-stone-500 italic mt-2">Nessuna istruzione specifica.</div>;
  }
  return (
    <div className="mt-2 space-y-1.5 text-xs">
      {forCheckout && hasBeds && (
        <div className="bg-white border border-stone-200 rounded p-2">
          <div className="font-bold text-stone-700 mb-1">🛏️ Configurazione letti:</div>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            {beds.matrimoniali > 0 && <span>· <b>{beds.matrimoniali}</b> matrimoniale{beds.matrimoniali > 1 ? "i" : ""}</span>}
            {beds.singoli > 0 && <span>· <b>{beds.singoli}</b> singol{beds.singoli > 1 ? "i" : "o"}</span>}
            {beds.letti_aggiunti > 0 && <span>· <b>{beds.letti_aggiunti}</b> letto{beds.letti_aggiunti > 1 ? "i" : ""} aggiunt{beds.letti_aggiunti > 1 ? "i" : "o"}</span>}
            {beds.culla && <span className="inline-flex items-center gap-1"><Baby className="w-3 h-3" /> culla</span>}
          </div>
        </div>
      )}
      {!forCheckout && hasCleaningFlags && (
        <div className="bg-white border border-stone-200 rounded p-2">
          <div className="font-bold text-stone-700 mb-1">🧽 Da fare:</div>
          <ul className="space-y-0.5 ml-2">
            {cl.asciugamani && <li>✓ Cambio asciugamani</li>}
            {cl.lenzuola && <li>✓ Cambio lenzuola</li>}
            {cl.veloce && <li>✓ Pulizia veloce</li>}
            {cl.profonda && <li>✓ Pulizia profonda</li>}
          </ul>
        </div>
      )}
      {notes && (
        <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
          <div className="font-bold text-yellow-800 mb-0.5">📝 Note dell&apos;host:</div>
          <div className="whitespace-pre-wrap">{notes}</div>
        </div>
      )}
    </div>
  );
};

export default function PublicCleaningPlanPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.get(`${API}/api/public/cleaning-plan/${encodeURIComponent(token)}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) {
          const code = e?.response?.status;
          if (code === 410) setError("Link scaduto. Chiedi all'host un nuovo link WhatsApp.");
          else if (code === 404) setError("Link non valido. Verifica di aver aperto il link giusto.");
          else setError("Errore di connessione. Riprova tra qualche secondo.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#AC5D45]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-6">
        <div className="max-w-md text-center bg-white border border-rose-200 rounded-xl p-6 shadow-sm" data-testid="cleaning-error">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h1 className="text-lg font-display text-[#1E3A5F] mb-2">Impossibile caricare il piano</h1>
          <p className="text-sm text-stone-600">{error}</p>
        </div>
      </div>
    );
  }

  const savedState = data?.saved_state?.rooms || {};
  const arrivals = data?.arrivals || [];
  const checkouts = data?.checkouts || [];
  const staying = data?.staying || [];
  const totalTasks = arrivals.length + checkouts.length + staying.length;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-8" data-testid="public-cleaning-plan">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#AC5D45] to-[#8A4936] text-white px-4 py-5 shadow-md">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-5 h-5" />
            <h1 className="font-display text-lg">Piano pulizie</h1>
          </div>
          <div className="text-xl font-display capitalize" data-testid="cleaning-date">{fmtDayLong(data?.date)}</div>
          <div className="text-xs opacity-90 mt-1">
            {totalTasks > 0
              ? `${totalTasks} camera${totalTasks > 1 ? "e" : ""} da gestire · B&B Emmanuel`
              : "Nessun lavoro previsto · B&B Emmanuel"}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-3">
        {totalTasks === 0 && (
          <div className="bg-white border border-emerald-200 rounded-xl p-6 text-center" data-testid="cleaning-none">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <div className="font-display text-lg text-emerald-800">Giornata libera!</div>
            <div className="text-sm text-stone-600 mt-1">Nessuna pulizia da fare oggi.</div>
          </div>
        )}

        {/* 🔴 CHECK-OUT: la sezione più importante — camere da rifare */}
        {checkouts.length > 0 && (
          <Section
            title="Check-out → CAMERE DA RIFARE"
            count={checkouts.length}
            color={{ bg: "bg-red-50", border: "border-red-200", text: "text-red-800", icon: "text-red-600", badge: "bg-red-600" }}
            defaultOpen={true}
            icon={LogOut}
            testId="cleaning-checkouts"
          >
            {checkouts.map((c) => {
              const st = savedState[c.code];
              return (
                <div key={c.code} className="border border-red-100 rounded-lg p-3 bg-red-50/30" data-testid={`checkout-${c.code}`}>
                  <div className="flex flex-wrap gap-x-2 items-baseline text-sm">
                    <span className="font-semibold text-[#1E3A5F]">🛏️ {c.room_name || "-"}</span>
                    {c.room_slot && <span className="text-stone-500">· vaschetta {c.room_slot}</span>}
                  </div>
                  <div className="text-xs text-stone-600 mt-0.5">Esce: <b>{c.guest_name}</b></div>
                  {c.next_guest ? (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                      <div className="font-bold text-amber-800">⚠️ Prossimo ospite (arriva {c.next_guest.check_in}):</div>
                      <div>{c.next_guest.guest_name} · {c.next_guest.counts.adults} adulti
                        {c.next_guest.counts.children > 0 && ` + ${c.next_guest.counts.children} bambini`}
                        {c.next_guest.counts.children_ages?.length > 0 && ` (${c.next_guest.counts.children_ages.join(", ")} anni)`}
                      </div>
                      {c.next_guest.special_requests?.length > 0 && (
                        <div className="mt-1"><b>Richieste:</b> {c.next_guest.special_requests.join(" · ")}</div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-stone-500 italic">Camera resta libera domani (nessun nuovo ospite)</div>
                  )}
                  <ReadOnlyTasks st={st} forCheckout={true} />
                </div>
              );
            })}
          </Section>
        )}

        {/* 🟢 ARRIVI */}
        {arrivals.length > 0 && (
          <Section
            title="Nuovi arrivi"
            count={arrivals.length}
            color={{ bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", icon: "text-emerald-600", badge: "bg-emerald-600" }}
            defaultOpen={true}
            icon={LogIn}
            testId="cleaning-arrivals"
          >
            {arrivals.map((a) => (
              <div key={a.code} className="border border-emerald-100 rounded-lg p-3 bg-emerald-50/40" data-testid={`arrival-${a.code}`}>
                <div className="flex flex-wrap gap-x-2 items-baseline text-sm">
                  <span className="font-semibold text-[#1E3A5F]">📍 {a.room_name || "-"}</span>
                  {a.room_slot && <span className="text-stone-500">· vaschetta {a.room_slot}</span>}
                  <span className="text-stone-500">·</span>
                  <span className="font-semibold">{a.guest_name}</span>
                  <span className="text-stone-500">·</span>
                  <span>{a.counts.adults} adulti{a.counts.children > 0 && ` + ${a.counts.children} bambini`}</span>
                  {a.counts.children_ages?.length > 0 && (
                    <span className="text-stone-500">({a.counts.children_ages.join(", ")} anni)</span>
                  )}
                </div>
                {a.arrival_time && <div className="text-xs text-stone-600 mt-1">arrivo previsto ore {a.arrival_time}</div>}
                {a.special_requests?.length > 0 && (
                  <div className="mt-1 text-xs bg-white border border-emerald-200 rounded p-2">
                    <b>Richieste ospite:</b> {a.special_requests.join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </Section>
        )}

        {/* 🟡 STAYING */}
        {staying.length > 0 && (
          <Section
            title="Camere occupate → RIFINITURA"
            count={staying.length}
            color={{ bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", icon: "text-amber-600", badge: "bg-amber-600" }}
            defaultOpen={false}
            icon={BedDouble}
            testId="cleaning-staying"
          >
            {staying.map((s) => {
              const st = savedState[s.code];
              return (
                <div key={s.code} className="border border-amber-100 rounded-lg p-3 bg-amber-50/40" data-testid={`staying-${s.code}`}>
                  <div className="flex flex-wrap gap-x-2 items-baseline text-sm">
                    <span className="font-semibold text-[#1E3A5F]">🛏️ {s.room_name || "-"}</span>
                    <span className="text-stone-500">—</span>
                    <span className="font-semibold">{s.guest_name}</span>
                    {s.nights?.night_num && (
                      <span className="text-stone-500">(notte {s.nights.night_num} di {s.nights.total_nights})</span>
                    )}
                  </div>
                  {s.special_requests?.length > 0 && (
                    <div className="mt-2 text-xs bg-white border border-amber-200 rounded p-2">
                      <b>Richieste ospite:</b> {s.special_requests.join(" · ")}
                    </div>
                  )}
                  <ReadOnlyTasks st={st} forCheckout={false} />
                </div>
              );
            })}
          </Section>
        )}

        {/* Footer info — link scade 3 giorni */}
        <div className="text-center text-xs text-stone-500 pt-4">
          <div>Per assistenza contatta l&apos;host · B&amp;B Emmanuel</div>
          <div className="mt-1 opacity-70">📞 +39 0965 181 7260</div>
        </div>
      </div>
    </div>
  );
}
